/**
 * =============================================================================
 * THE LIVE ACTIVITY FEED — AND WHY `EventSource`'s OWN RECONNECT IS UNUSABLE
 * =============================================================================
 *
 * The stream is opened as `GET /api/v1/events/stream?ticket=…`, because a browser
 * `EventSource` cannot send an `Authorization` header. The ticket is minted by an
 * authenticated `POST /events/ticket`, lives 60 seconds, and is **SINGLE-USE**: the
 * server burns its `jti` on redemption (`sse-ticket.service.ts`).
 *
 * That single fact makes `EventSource`'s built-in reconnect a guaranteed dead end,
 * and the P2-T0 gate called it out as a mandatory design consequence. `EventSource`
 * reconnects by re-requesting THE SAME URL — including the same spent ticket. The
 * server answers 401. Per the HTML spec, a non-2xx on a reconnect attempt **fails
 * the connection permanently**: `EventSource` sets `readyState = CLOSED` and never
 * tries again. So the very first network blip, laptop-lid close or proxy timeout
 * would silently kill the feed for the rest of the session, with no error visible
 * anywhere.
 *
 * So this hook does the reconnecting itself:
 *   1. mint a FRESH ticket (`POST /events/ticket`, which goes through the API
 *      client and therefore through the single-flight refresh mutex);
 *   2. open an `EventSource` on the returned `streamUrl`;
 *   3. on ANY error, close the connection immediately — do not let the native
 *      reconnect fire with a burned ticket — and schedule attempt N+1 with
 *      exponential backoff plus jitter;
 *   4. on a clean open, reset the backoff.
 *
 * Backoff is capped at 30s and jittered so that N tabs of the same app do not all
 * re-mint at the same instant after a server restart.
 *
 * THE CONNECTION STATE IS REPORTED, NOT HIDDEN. A feed that has silently stopped
 * updating is indistinguishable from a quiet day, and on a dashboard that is the
 * difference between "nothing happened" and "you are looking at stale data".
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { absoluteStreamUrl, mintSseTicket } from '../../lib/api';
import type { EventRow } from '../../domain/events';

export type StreamState = 'connecting' | 'live' | 'reconnecting' | 'failed';

export interface EventStream {
  /** Newest first. Capped — see `MAX_BUFFERED`. */
  events: readonly EventRow[];
  state: StreamState;
  /** Retry now, resetting the backoff. Wired to the "reconnect" affordance. */
  reconnect: () => void;
}

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
/** Beyond this the feed is declared failed and stops trying on its own. */
const MAX_ATTEMPTS = 8;
/**
 * The widget shows six rows (Figma 12:3103). Buffering a few more absorbs a burst
 * without letting a long-lived tab grow without bound.
 */
const MAX_BUFFERED = 50;

function backoffMs(attempt: number): number {
  const exponential = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
  // Full jitter: spread simultaneous reconnects from many tabs.
  return Math.round(exponential / 2 + Math.random() * (exponential / 2));
}

function isEventRow(value: unknown): value is EventRow {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Partial<EventRow>;
  return (
    typeof row.id === 'string' &&
    typeof row.verb === 'string' &&
    typeof row.occurred_at === 'string'
  );
}

/**
 * Subscribe to the country event stream.
 *
 * `countryCode` is not a filter this hook applies — the TICKET carries the scope,
 * baked in at mint time from the caller's token, and the server filters every frame
 * against it. It is a dependency only so that switching country tears the stream
 * down and mints a ticket for the new scope.
 */
export function useEventStream(countryCode: string, enabled = true): EventStream {
  const [events, setEvents] = useState<readonly EventRow[]>([]);
  const [state, setState] = useState<StreamState>(enabled ? 'connecting' : 'failed');

  const sourceRef = useRef<EventSource | null>(null);
  const timerRef = useRef<number | null>(null);
  const attemptRef = useRef(0);
  const cancelledRef = useRef(false);
  const nonceRef = useRef(0);

  const teardown = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (sourceRef.current !== null) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  const connect = useCallback(
    async function connect(): Promise<void> {
      if (cancelledRef.current) return;
      const nonce = nonceRef.current;

      let streamUrl: string;
      try {
        // A FRESH TICKET PER ATTEMPT. This is the whole point of the hook.
        const ticket = await mintSseTicket();
        streamUrl = absoluteStreamUrl(ticket.streamUrl);
      } catch {
        // Could not even mint: the API is down, or the session is gone (in which
        // case the client has already routed to login). Back off and try again.
        scheduleRetry();
        return;
      }
      if (cancelledRef.current || nonce !== nonceRef.current) return;

      const source = new EventSource(streamUrl);
      sourceRef.current = source;

      source.addEventListener('ready', () => {
        if (nonce !== nonceRef.current) return;
        attemptRef.current = 0;
        setState('live');
      });

      source.addEventListener('event', (message: MessageEvent<string>) => {
        if (nonce !== nonceRef.current) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(message.data) as unknown;
        } catch {
          // A frame we cannot read is dropped, not guessed at.
          return;
        }
        if (!isEventRow(parsed)) return;
        setEvents((previous) => {
          // The stream can replay a frame across a reconnect; dedupe by id so the
          // feed never shows the same fact twice.
          if (previous.some((row) => row.id === parsed.id)) return previous;
          return [parsed, ...previous].slice(0, MAX_BUFFERED);
        });
      });

      // `heartbeat` frames arrive every 25s (SSE_HEARTBEAT_MS). They are not
      // rendered; their value is that a socket carrying them is provably alive.

      source.onerror = () => {
        if (nonce !== nonceRef.current) return;
        // CLOSE IT OURSELVES. Left open, `EventSource` would retry this URL with a
        // ticket the server has already burned, get a 401, and close permanently.
        source.close();
        if (sourceRef.current === source) sourceRef.current = null;
        scheduleRetry();
      };

      function scheduleRetry(): void {
        if (cancelledRef.current || nonce !== nonceRef.current) return;
        if (attemptRef.current >= MAX_ATTEMPTS) {
          setState('failed');
          return;
        }
        const delay = backoffMs(attemptRef.current);
        attemptRef.current += 1;
        setState('reconnecting');
        timerRef.current = window.setTimeout(() => {
          void connect();
        }, delay);
      }
    },
    [],
  );

  const reconnect = useCallback(() => {
    teardown();
    attemptRef.current = 0;
    nonceRef.current += 1;
    cancelledRef.current = false;
    setState('connecting');
    void connect();
  }, [connect, teardown]);

  useEffect(() => {
    if (!enabled) {
      setState('failed');
      return undefined;
    }
    cancelledRef.current = false;
    attemptRef.current = 0;
    nonceRef.current += 1;
    setEvents([]);
    setState('connecting');
    void connect();

    return () => {
      cancelledRef.current = true;
      nonceRef.current += 1;
      teardown();
    };
    // `countryCode` is in the dependency list on purpose: a country switch must
    // mint a ticket for the new scope rather than keep filtering the old stream.
  }, [countryCode, enabled, connect, teardown]);

  return { events, state, reconnect };
}
